import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Badge,
  Chip,
  Alert,
  Button,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Google as GoogleIcon,
  Microsoft as BingIcon,
  CheckCircle as ConnectedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  TrendingUp,
  Info as InfoIcon,
  HourglassEmpty as InProgressIcon,
  Lock as LockedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useTimeline } from '../hooks/useTimeline';
import { EngineTimeline, StageStatus } from '../types/timeline.types';
import IndexingStatusWidget from './IndexingStatusWidget';

interface TabbedTimelineWidgetProps {
  siteId: number;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`engine-tabpanel-${index}`}
      aria-labelledby={`engine-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TabbedTimelineWidget: React.FC<TabbedTimelineWidgetProps> = ({ 
  siteId, 
  collapsible = false,
  defaultExpanded = true 
}) => {
  const { data: timeline, isLoading } = useTimeline(siteId);
  const [activeTab, setActiveTab] = useState(0);
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (isLoading || !timeline) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Asneris Search Growth Journey™
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  const getEngineIcon = (engine: string) => {
    switch (engine) {
      case 'GOOGLE': return <GoogleIcon />;
      case 'BING': return <BingIcon />;
      default: return <InfoIcon />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE': return <ConnectedIcon color="success" />;
      case 'CONNECTED_NO_DATA': return <WarningIcon color="warning" />;
      case 'NOT_CONNECTED': return <ErrorIcon color="error" />;
      default: return <InfoIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'CONNECTED_NO_DATA': return 'warning';
      case 'NOT_CONNECTED': return 'error';
      default: return 'default';
    }
  };

  const getStageIcon = (status: StageStatus) => {
    switch (status) {
      case StageStatus.COMPLETE: return <ConnectedIcon color="success" />;
      case StageStatus.IN_PROGRESS: return <InProgressIcon color="warning" />;
      case StageStatus.LOCKED: return <LockedIcon color="disabled" />;
      default: return <InfoIcon color="info" />;
    }
  };

  const engines = [
    timeline.engineTimelines.googleTimeline,
    timeline.engineTimelines.bingTimeline
  ].filter(Boolean);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderEngineTimeline = (engineTimeline: EngineTimeline) => {
    if (engineTimeline.status === 'NOT_CONNECTED') {
      return (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {engineTimeline.engineName} Not Connected
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Connect {engineTimeline.engineName} to unlock comprehensive SEO insights and timeline tracking.
            </Typography>
            {engineTimeline.setupInstructions && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" fontWeight="medium">Setup Instructions:</Typography>
                <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
                  {engineTimeline.setupInstructions}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
              size="small"
            >
              Connect {engineTimeline.engineName}
            </Button>
          </Alert>
        </Box>
      );
    }

    const activeStep = engineTimeline.stages?.findIndex(s => s.status !== StageStatus.COMPLETE) ?? -1;

    return (
      <Box>
        {/* Engine Status */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
              {getEngineIcon(engineTimeline.engine)}
              {engineTimeline.engineName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={-1} mb={1}>
              {engineTimeline.journeyName}
            </Typography>
            <Chip 
              icon={getStatusIcon(engineTimeline.status)}
              label={engineTimeline.status.replace('_', ' ')}
              color={getStatusColor(engineTimeline.status) as any}
              size="small"
            />
          </Box>

          {/* Progress */}
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="medium">
                Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {engineTimeline.progressPercent}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={engineTimeline.progressPercent} 
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Box mt={1} display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {engineTimeline.dataDays} days of data
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Next: {engineTimeline.nextMilestone}
              </Typography>
            </Box>
          </Box>

          {/* Unlocked Features */}
          {engineTimeline.unlockedFeatures.length > 0 && (
            <Box mb={2}>
              <Typography variant="body2" fontWeight="medium" mb={1}>
                Unlocked Features
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {engineTimeline.unlockedFeatures.map((feature, index) => (
                  <Chip 
                    key={index}
                    label={feature} 
                    size="small" 
                    color="success"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Page Indexing Status - Critical for early stage owners */}
          {engineTimeline.indexingStatus && (
            <Box mb={3}>
              <IndexingStatusWidget 
                indexingStatus={engineTimeline.indexingStatus}
                engineName={engineTimeline.engineName}
              />
            </Box>
          )}
        </Box>

        {/* Stages Stepper */}
        {engineTimeline.stages?.length > 0 && (
          <Stepper activeStep={activeStep} orientation="vertical">
            {engineTimeline.stages?.map((stage) => (
              <Step key={stage.stageType} expanded>
                <StepLabel
                  StepIconComponent={() => getStageIcon(stage.status)}
                  optional={
                    stage.status !== StageStatus.COMPLETE && (
                      <Typography variant="caption">
                        {stage.status === StageStatus.IN_PROGRESS 
                          ? `~${stage.estimatedDaysRemaining} days remaining`
                          : stage.unlockCondition
                        }
                      </Typography>
                    )
                  }
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight={stage.status === StageStatus.IN_PROGRESS ? 'bold' : 'normal'}>
                      {stage.stageName}
                    </Typography>
                    <Chip 
                      label={stage.status} 
                      size="small" 
                      color={stage.status === StageStatus.COMPLETE ? 'success' : stage.status === StageStatus.IN_PROGRESS ? 'warning' : 'default'}
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {stage.description}
                  </Typography>
                  
                  {stage.status === StageStatus.IN_PROGRESS && (
                    <Box mb={2}>
                      <LinearProgress 
                        variant="determinate" 
                        value={stage.progressPercent} 
                        sx={{ height: 4, borderRadius: 1 }}
                      />
                    </Box>
                  )}

                  <Tooltip title={stage.whyItMatters} arrow placement="right">
                    <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
                      <InfoIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Why this matters
                      </Typography>
                    </Box>
                  </Tooltip>

                  {stage.completedDate && (
                    <Typography variant="caption" display="block" color="success.main" mt={1}>
                      ✓ Completed {new Date(stage.completedDate).toLocaleDateString()}
                    </Typography>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box display="flex" alignItems="center" justifyContent="between" mb={2} flexWrap="wrap" gap={1}>
          <Box display="flex" flexDirection="column" gap={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUp color="primary" />
              <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
                Asneris Search Growth Journey™
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
              Track your progress across Google Search Console and Bing Webmaster Tools
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label={`Day ${timeline.siteAgeDays}`} 
              size="small" 
              color="primary"
              variant="outlined"
            />
            {collapsible && (
              <IconButton 
                size="medium" 
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Collapse timeline' : 'Expand timeline'}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        <Collapse in={expanded || !collapsible}>
          {/* Recommendation Banner */}
          {timeline.engineTimelines.recommendedNextEngine && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="medium">
                Recommended Next Step
              </Typography>
              <Typography variant="body2">
                Connect {timeline.engineTimelines.recommendedNextEngine === 'GOOGLE' ? 'Google Search Console' :
                         timeline.engineTimelines.recommendedNextEngine === 'BING' ? 'Bing Webmaster Tools' :
                         timeline.engineTimelines.recommendedNextEngine} for comprehensive SEO insights.
              </Typography>
            </Alert>
          )}

          {/* Engine Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="engine timeline tabs">
              {engines.map((engine) => (
                <Tab
                  key={engine.engine}
                  icon={getEngineIcon(engine.engine)}
                  iconPosition="start"
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Badge
                        color={engine.status === 'ACTIVE' ? 'success' : engine.status === 'CONNECTED_NO_DATA' ? 'warning' : 'error'}
                        variant="dot"
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {engine.engine === 'GOOGLE' ? 'Google Search Journey' : 
                           engine.engine === 'BING' ? 'Bing Search Journey' : 'Visitor Analytics Journey'}
                        </Typography>
                      </Badge>
                      {engine.hasData && (
                        <Chip
                          label={`${engine.progressPercent}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 18 }}
                        />
                      )}
                    </Box>
                  }
                  sx={{ minHeight: 60 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          {engines.map((engine, index) => (
            <TabPanel key={engine.engine} value={activeTab} index={index}>
              {renderEngineTimeline(engine)}
            </TabPanel>
          ))}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TabbedTimelineWidget;